import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import { TREATMENT_KINDS } from '@lensmira/domain'

import { useTranslation } from '../i18n.js'
import { isTintKind, treatmentKindLabel } from '../status-labels.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * Putting a coating or a tint on file. §6.4 `CAT-012`.
 *
 * `CAT-012` `MUST`: treatments are "separate, stackable, individually priced entities", and the
 * requirement enumerates eleven kinds.
 *
 * **Nothing could write one, and the counter had nowhere to pick one from.** All seven tenants on
 * the deployment held `treatments=0`, and `NewOrder` offers only a free-text treatment-ids box with
 * no ids to type into it. So every spectacle lens a shop sold went out uncoated as far as Lensmira
 * was concerned: nothing to price on the quote, and `job-ticket-read.service.ts` with no row to
 * label, so the lab document named no treatment. In Pakistan an AR coat is a routine upsell, which
 * makes this revenue the system could not record rather than a cosmetic gap.
 *
 * **Colour and density appear only for a tint.** `CAT-012` makes them a tint's properties and the
 * server refuses them elsewhere, because on an AR coating they are figures nothing reads and the
 * lab ticket would print a colour for a coating that has none.
 */

export interface TreatmentCatalogueProps {
  /** Appendix B's `Catalogue - edit`, owner and manager. */
  readonly canEdit: boolean
  readonly onWritten?: () => void
}

export function TreatmentCatalogue({
  canEdit,
  onWritten,
}: TreatmentCatalogueProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [kind, setKind] = useState<string>('ar_coating')
  const [brand, setBrand] = useState('')
  const [grade, setGrade] = useState('')
  const [colour, setColour] = useState('')
  const [densityPercent, setDensityPercent] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  const isTint = isTintKind(kind)

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setDone(t('treatmentCatalogue.saved', { kind: treatmentKindLabel(t, kind) }))
    setBrand('')
    setGrade('')
    setColour('')
    setDensityPercent('')
    onWritten?.()
  })

  const save = useCallback(async () => {
    if (isTint && colour.trim() === '') {
      // A tint with no colour is a row a dispenser cannot choose between, and §13's shops stock
      // several at once. The server refuses it too.
      setError(t('treatmentCatalogue.colourRequired'))
      return
    }
    setError(null)
    setDone(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/catalogue/treatments',
      label: t('treatmentCatalogue.queueLabel', { kind: treatmentKindLabel(t, kind) }),
      body: {
        kind,
        ...(brand.trim() === '' ? {} : { brand: brand.trim() }),
        ...(grade.trim() === '' ? {} : { grade: grade.trim() }),
        // Only for a tint, because the server refuses either on anything else.
        ...(isTint && colour.trim() !== '' ? { colour: colour.trim() } : {}),
        ...(isTint && densityPercent.trim() !== ''
          ? { densityPercent: Number(densityPercent.trim()) }
          : {}),
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [brand, colour, densityPercent, enqueue, grade, isTint, kind, t])

  if (!canEdit) {
    return (
      <section className="lm-setup-panel" aria-labelledby="treatment-catalogue-heading">
        <h2 id="treatment-catalogue-heading" className="lm-setup-panel__heading">
          {t('treatmentCatalogue.heading')}
        </h2>
        <p className="lm-hint">{t('treatmentCatalogue.notYours')}</p>
      </section>
    )
  }

  return (
    <section className="lm-setup-panel" aria-labelledby="treatment-catalogue-heading">
      <h2 id="treatment-catalogue-heading" className="lm-setup-panel__heading">
        {t('treatmentCatalogue.heading')}
      </h2>
      <p className="lm-setup-panel__effects">{t('treatmentCatalogue.means')}</p>

      <label className="lm-field">
        <span className="lm-field__label">{t('treatmentCatalogue.kind')}</span>
        <select className="lm-input" value={kind} onChange={(e) => setKind(e.currentTarget.value)}>
          {TREATMENT_KINDS.map((value) => (
            <option key={value} value={value}>
              {treatmentKindLabel(t, value)}
            </option>
          ))}
        </select>
      </label>

      <label className="lm-field">
        <span className="lm-field__label">{t('treatmentCatalogue.brand')}</span>
        <input
          className="lm-input"
          dir="auto"
          value={brand}
          autoComplete="off"
          onChange={(e) => setBrand(e.currentTarget.value)}
        />
      </label>

      <label className="lm-field">
        <span className="lm-field__label">{t('treatmentCatalogue.grade')}</span>
        {/* `CAT-012`: "AR coating (by brand and grade)", which is why both are fields. */}
        <input
          className="lm-input"
          dir="auto"
          value={grade}
          autoComplete="off"
          onChange={(e) => setGrade(e.currentTarget.value)}
        />
      </label>

      {isTint ? (
        <>
          <label className="lm-field">
            <span className="lm-field__label">{t('treatmentCatalogue.colour')}</span>
            {/* `CAT-012`: "solid/gradient tint (by colour and density %)". */}
            <input
              className="lm-input"
              dir="auto"
              value={colour}
              autoComplete="off"
              onChange={(e) => setColour(e.currentTarget.value)}
            />
          </label>
          <label className="lm-field">
            <span className="lm-field__label">{t('treatmentCatalogue.density')}</span>
            <input
              className="lm-input"
              dir="ltr"
              inputMode="numeric"
              value={densityPercent}
              autoComplete="off"
              placeholder="65"
              onChange={(e) => setDensityPercent(e.currentTarget.value)}
            />
          </label>
        </>
      ) : null}

      <button
        type="button"
        className="lm-button lm-button--primary"
        disabled={writeId !== null}
        onClick={() => {
          void save()
        }}
      >
        {t('treatmentCatalogue.save')}
      </button>

      {error === null ? null : (
        <p className="lm-error" role="alert">
          {error}
        </p>
      )}
      {done === null ? null : (
        <p className="lm-hint" role="status">
          {done}
        </p>
      )}
      {writeId === null ? null : (
        <p className="lm-hint" role="status">
          {statusLine(t, snapshot, writeId, t('treatmentCatalogue.queueSubject'))}
        </p>
      )}
    </section>
  )
}
